import { useState } from "react";
import { Ban, Check, Copy, KeyRound, Link2, Mail, ShieldAlert, UserMinus, UserPlus, Users } from "lucide-react";

import { ErrorApi } from "../api/cliente.ts";
import { ErrorConsulta, EsqueletoLista } from "../componentes/Errores.tsx";
import type { Invitacion, Miembro, Rol } from "../api/tipos.ts";
import {
  usarAnularInvitacion,
  usarCambiarRol,
  usarEquipo,
  usarInvitar,
  usarQuitarDelEquipo,
} from "../ganchos/datos.ts";
import { PERMISO, ROLES, nombreRol } from "../sesion/permisos.ts";
import { usarSesion } from "../sesion/ContextoSesion.tsx";
import { fechaHora, haceRato, inicial } from "../formato.ts";
import { Aviso, Boton, Campo, Dialogo, Seccion, Tarjeta, cx } from "../componentes/ui.tsx";

type Tipo = "invitacion" | "restablecimiento";

/**
 * Invitar y restablecer.
 *
 * El backend devuelve el token **una sola vez** y después solo guarda su hash,
 * así que esta pantalla tiene una responsabilidad incómoda: mostrarlo de forma
 * que quien lo genera lo copie ahora. Perderlo no es recuperable — se genera
 * otro, y ya.
 *
 * Y no, no lo envía por correo: el proveedor de correo saliente es una decisión
 * abierta del proyecto (D-14). Fingir aquí que se envió sería mentir en la
 * interfaz.
 */
export function Equipo() {
  const { puedo } = usarSesion();
  const puedeGestionar = puedo(PERMISO.INVITAR);
  const invitar = usarInvitar();
  const [correo, setCorreo] = useState("");
  const [rol, setRol] = useState<Rol>("cajero");
  const [tipo, setTipo] = useState<Tipo>("invitacion");
  const [emitida, setEmitida] = useState<Invitacion | null>(null);

  function enviar(e: React.FormEvent) {
    e.preventDefault();
    setEmitida(null);
    invitar.mutate(
      { correo: correo.trim(), rol, tipo },
      {
        onSuccess: (inv) => {
          setEmitida(inv);
          setCorreo("");
        },
      },
    );
  }

  return (
    <Seccion
      titulo="Equipo"
      descripcion={
        puedeGestionar
          ? "Quién puede entrar a esta cuenta. Dale acceso a alguien, o genérale un enlace para que vuelva a poner su contraseña."
          : "Quién puede entrar a esta cuenta. Cambiarlo es cosa del dueño."
      }
    >
      <QuienTieneAcceso />

      {!puedeGestionar ? null : (
      <div className="grid gap-6 lg:grid-cols-5">
        <Tarjeta className="space-y-5 p-6 lg:col-span-3">
          <div className="flex gap-1 rounded-xl border border-borde bg-superficie-2 p-1">
            {(
              [
                ["invitacion", "Invitar a alguien", UserPlus],
                ["restablecimiento", "Restablecer contraseña", KeyRound],
              ] as const
            ).map(([valor, texto, Icono]) => (
              <button
                key={valor}
                type="button"
                onClick={() => {
                  setTipo(valor);
                  setEmitida(null);
                }}
                className={cx(
                  "flex flex-1 items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
                  tipo === valor
                    ? "bg-superficie text-tinta shadow-plano"
                    : "text-tenue hover:text-tinta",
                )}
              >
                <Icono className="size-4" aria-hidden />
                {texto}
              </button>
            ))}
          </div>

          <form className="space-y-4" onSubmit={enviar}>
            <Campo
              etiqueta="Correo"
              type="email"
              icono={Mail}
              required
              placeholder="persona@empresa.co"
              value={correo}
              onChange={(e) => setCorreo(e.target.value)}
              ayuda={
                tipo === "restablecimiento"
                  ? "Debe ser el correo con el que ya entra a esta cuenta."
                  : undefined
              }
            />

            {tipo === "invitacion" && (
              <fieldset className="space-y-2">
                <legend className="mb-2 text-sm font-medium">¿Con qué rol?</legend>
                <div className="grid gap-2 sm:grid-cols-2">
                  {ROLES.map((r) => (
                    <label
                      key={r.valor}
                      className={cx(
                        "flex cursor-pointer gap-3 rounded-xl border p-3 transition-colors",
                        rol === r.valor
                          ? "border-marca bg-marca-suave"
                          : "border-borde hover:border-borde-fuerte",
                      )}
                    >
                      <input
                        type="radio"
                        name="rol"
                        value={r.valor}
                        checked={rol === r.valor}
                        onChange={() => setRol(r.valor)}
                        className="mt-1 size-4 shrink-0 accent-[var(--color-marca)]"
                      />
                      <span className="min-w-0">
                        <span className="block text-sm font-medium">{r.nombre}</span>
                        <span className="block text-xs leading-relaxed text-tenue">
                          {r.descripcion}
                        </span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>
            )}

            {invitar.error && (
              <Aviso titulo="No se pudo generar">
                {invitar.error instanceof ErrorApi
                  ? invitar.error.message
                  : "Error al hablar con 4drando."}
              </Aviso>
            )}

            <Boton
              type="submit"
              variante="primario"
              icono={tipo === "invitacion" ? UserPlus : KeyRound}
              cargando={invitar.isPending}
            >
              {tipo === "invitacion" ? "Generar invitación" : "Generar enlace"}
            </Boton>
          </form>
        </Tarjeta>

        <div className="space-y-4 lg:col-span-2">
          {emitida ? (
            <EnlaceEmitido invitacion={emitida} />
          ) : (
            <Tarjeta className="space-y-3 p-6 text-sm leading-relaxed text-tenue">
              <h3 className="font-semibold text-tinta">Cómo funciona</h3>
              <p>
                4drando genera un enlace y lo muestra <strong className="text-tinta">una sola vez</strong>.
                De ahí en adelante solo guarda una huella suya, así que ni nosotros podemos
                recuperarlo.
              </p>
              <p>
                Se lo haces llegar tú, por el canal que ya usas con esa persona. El enlace caduca, y
                usarlo una vez lo invalida.
              </p>
              <p>
                Quien lo abra pone su propia contraseña — mínimo 12 caracteres. Nadie más la conoce,
                tú incluido.
              </p>
            </Tarjeta>
          )}
        </div>
      </div>
      )}
    </Seccion>
  );
}

function EnlaceEmitido({ invitacion }: { invitacion: Invitacion }) {
  const [copiado, setCopiado] = useState(false);
  const enlace = `${location.origin}/invitacion?token=${encodeURIComponent(invitacion.token)}`;

  async function copiar() {
    try {
      await navigator.clipboard.writeText(enlace);
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch {
      // Sin permiso de portapapeles el usuario todavía puede seleccionarlo a mano.
      setCopiado(false);
    }
  }

  return (
    <Tarjeta className="space-y-4 border-marca/30 p-6 subir">
      <div className="flex items-start gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-xl bg-marca-suave text-marca-tinta">
          <Link2 className="size-4.5" aria-hidden />
        </span>
        <div>
          <h3 className="font-semibold">Enlace listo</h3>
          <p className="text-sm text-tenue">
            Para {invitacion.correo} · caduca el {fechaHora(invitacion.expiraEn)}
          </p>
        </div>
      </div>

      <textarea
        readOnly
        rows={3}
        value={enlace}
        onFocus={(e) => e.currentTarget.select()}
        className="cifra w-full resize-none rounded-xl border border-borde bg-superficie-2 p-3 text-xs break-all text-tenue focus:border-marca focus:outline-none"
      />

      <Boton
        variante={copiado ? "exito" : "primario"}
        icono={copiado ? Check : Copy}
        bloque
        onClick={() => void copiar()}
      >
        {copiado ? "Copiado" : "Copiar enlace"}
      </Boton>

      <Aviso tono="aviso" titulo="No lo vas a volver a ver">
        Si cierras esta pantalla sin copiarlo, tendrás que generar otro.
      </Aviso>
    </Tarjeta>
  );
}

/**
 * Quién tiene acceso a esta cuenta (SPEC-018).
 *
 * Va ARRIBA de la invitación porque a esta pantalla se viene a mirar quién más
 * toca los pagos; invitar es lo que se hace después. Hasta ahora esto no
 * existía: la puerta solo abría.
 */
function QuienTieneAcceso() {
  const { data, isPending, error, refetch } = usarEquipo();
  const cambiarRol = usarCambiarRol();
  const quitar = usarQuitarDelEquipo();
  const anular = usarAnularInvitacion();
  const [porQuitar, setPorQuitar] = useState<Miembro | null>(null);

  if (error) return <ErrorConsulta error={error} alReintentar={() => void refetch()} />;
  if (isPending) return <EsqueletoLista filas={3} />;
  if (!data) return null;

  const duenos = data.miembros.filter((m) => m.rol === "dueno").length;
  /**
   * El último dueño no se puede quitar NI bajar de rango. Es la misma regla por
   * dos puertas, y sin ella la cuenta se queda sin quien la administre: no hay
   * superusuario que la rescate, porque el aislamiento por cliente es real.
   */
  const esUltimoDueno = (m: Miembro) => m.rol === "dueno" && duenos === 1;

  const fallo = [cambiarRol.error, quitar.error, anular.error].find(Boolean);

  return (
    <div className="space-y-4">
      {fallo && (
        <Aviso titulo="No se pudo cambiar">
          {fallo instanceof ErrorApi ? fallo.message : "Error al hablar con 4drando."}
        </Aviso>
      )}

      <Tarjeta className="overflow-hidden">
        <div className="flex items-center gap-3 border-b border-borde p-5">
          <Users className="size-4.5 text-tenue" aria-hidden />
          <div>
            <h2 className="font-semibold tracking-tight">Quién tiene acceso</h2>
            <p className="text-sm text-tenue">
              {data.miembros.length === 1
                ? "1 persona puede entrar a esta cuenta."
                : `${data.miembros.length} personas pueden entrar a esta cuenta.`}
            </p>
          </div>
        </div>

        <ul className="divide-y divide-borde">
          {data.miembros.map((m) => (
            <li key={m.id} className="flex flex-wrap items-center gap-4 p-5">
              <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-marca-suave text-sm font-semibold text-marca-tinta">
                {inicial(m.correo)}
              </span>

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {m.correo}
                  {m.id === data.yo && <span className="ml-2 text-xs text-tenue">— tú</span>}
                </p>
                <p className="text-xs text-tenue">
                  Desde {fechaHora(m.desde)} ·{" "}
                  {m.ultimo_acceso ? (
                    <>entró {haceRato(m.ultimo_acceso)}</>
                  ) : (
                    <span className="text-aviso-tinta">nunca ha entrado</span>
                  )}
                </p>
              </div>

              {data.puedeGestionar ? (
                <>
                  <select
                    aria-label={`Rol de ${m.correo}`}
                    value={m.rol}
                    disabled={esUltimoDueno(m) || cambiarRol.isPending}
                    onChange={(e) =>
                      cambiarRol.mutate({ usuarioId: m.id, rol: e.target.value as Rol })
                    }
                    className="h-10 rounded-xl border border-borde bg-superficie px-3 text-sm text-tinta disabled:cursor-not-allowed disabled:text-tenue-2 focus:border-marca focus:ring-4 focus:ring-marca/12 focus:outline-none"
                  >
                    {ROLES.map((r) => (
                      <option key={r.valor} value={r.valor}>
                        {r.nombre}
                      </option>
                    ))}
                  </select>

                  <Boton
                    variante="fantasma"
                    icono={UserMinus}
                    disabled={esUltimoDueno(m) || quitar.isPending}
                    title={
                      esUltimoDueno(m)
                        ? "Es el último dueño. Nombra a otro antes."
                        : "Quitarle el acceso"
                    }
                    onClick={() => setPorQuitar(m)}
                  >
                    Quitar
                  </Boton>
                </>
              ) : (
                <span className="text-sm text-tenue">{nombreRol(m.rol)}</span>
              )}
            </li>
          ))}
        </ul>

        {duenos === 1 && data.puedeGestionar && (
          <p className="flex max-w-prose items-start gap-2 border-t border-borde bg-superficie-2/60 p-4 text-xs leading-relaxed text-tenue">
            <ShieldAlert className="mt-0.5 size-4 shrink-0 text-aviso" aria-hidden />
            <span>
              Hay un solo dueño, así que no puede quitarse ni bajarse de rol: la cuenta se quedaría
              sin quien la administre y no hay forma de recuperarla desde aquí. Nombra a otro dueño
              antes de irte.
            </span>
          </p>
        )}
      </Tarjeta>

      {data.invitaciones.length > 0 && (
        <Tarjeta className="overflow-hidden">
          <div className="border-b border-borde p-5">
            <h2 className="font-semibold tracking-tight">Invitaciones sin usar</h2>
            <p className="text-sm text-tenue">
              Acceso ya concedido que todavía nadie ha estrenado. Anular una la invalida al instante.
            </p>
          </div>
          <ul className="divide-y divide-borde">
            {data.invitaciones.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center gap-4 p-5">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium">{i.correo}</p>
                  <p className="text-xs text-tenue">
                    {i.tipo === "restablecimiento" ? "Restablecer contraseña" : nombreRol(i.rol)} ·
                    caduca {fechaHora(i.expira_en)}
                  </p>
                </div>
                {data.puedeGestionar && (
                  <Boton
                    variante="fantasma"
                    icono={Ban}
                    disabled={anular.isPending}
                    onClick={() => anular.mutate({ id: i.id })}
                  >
                    Anular
                  </Boton>
                )}
              </li>
            ))}
          </ul>
        </Tarjeta>
      )}

      <Dialogo
        abierto={porQuitar !== null}
        alCerrar={() => setPorQuitar(null)}
        titulo="¿Quitarle el acceso?"
      >
        <div className="space-y-3 text-sm leading-relaxed text-tenue">
          <p>
            <span className="font-medium text-tinta">{porQuitar?.correo}</span> dejará de entrar a
            esta cuenta <span className="font-medium text-tinta">de inmediato</span>, aunque tenga la
            sesión abierta.
          </p>
          <p>
            Sus decisiones se quedan donde están y siguen firmadas con su nombre. Y si trabaja con
            otras cuentas, esas no se tocan.
          </p>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Boton type="button" onClick={() => setPorQuitar(null)}>
            Cancelar
          </Boton>
          <Boton
            type="button"
            variante="peligro"
            icono={UserMinus}
            cargando={quitar.isPending}
            onClick={() => {
              const m = porQuitar;
              setPorQuitar(null);
              if (m) quitar.mutate({ usuarioId: m.id });
            }}
          >
            Quitar el acceso
          </Boton>
        </div>
      </Dialogo>
    </div>
  );
}
